import { ShiftManagementSelectableEmployeesDocument } from 'generated/resources/graphql'
import type { ShiftManagementSelectableEmployeesQuery } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

type PermissionAction = 'read' | 'create'

export type SelectableEmployee = Pick<
  ShiftManagementSelectableEmployeesQuery['shiftManagement']['employeesPermissions'][number],
  'id' | 'fullName'
>

// Employees USING SHIFT MANAGEMENT + that I have permissions to read/create shifts
const useSelectableEmployees = (
  startAt: string,
  endAt: string,
  action: PermissionAction = 'create',
  locationIds?: number[]
): {
  loading: boolean
  selectableEmployeeIds: number[]
  selectableEmployees: SelectableEmployee[]
} => {
  const isRead = action === 'read'
  // Cache-first to avoid fetching the same data multiple times
  // because SelectableEmployees is stable and doesn't change frequently.
  //
  // This returns employees using shiftmanagement, but I might not have permissions for them
  const { data: selectableEmployeesData, loading: selectableEmployeesLoading } = useQuery(
    ShiftManagementSelectableEmployeesDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        startAt,
        endAt,
        locationIds,
      },
    }
  )

  const { data: terminatedEmployeesData, loading: terminatedEmployeesLoading } = useQuery(
    ShiftManagementSelectableEmployeesDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        startAt,
        endAt,
        terminated: true,
        locationIds,
      },
      skip: !isRead,
    }
  )

  const employeesPermissions = selectableEmployeesData?.shiftManagement.employeesPermissions
  const selectableEmployeeIds: number[] = []
  const selectableEmployees =
    employeesPermissions?.reduce<SelectableEmployee[]>((acc, employee) => {
      if (isRead || employee.permissions?.create) {
        selectableEmployeeIds.push(employee.id)
        acc.push({ id: employee.id, fullName: employee.fullName })
      }
      return acc
    }, []) ?? []

  const terminatedEmployeeIds =
    terminatedEmployeesData?.shiftManagement.employeesPermissions?.reduce<number[]>(
      (acc, employee) => {
        if (isRead) acc.push(employee.id)
        return acc
      },
      []
    ) ?? []

  const terminatedEmployees =
    terminatedEmployeesData?.shiftManagement.employeesPermissions?.reduce<SelectableEmployee[]>(
      (acc, employee) => {
        if (isRead) acc.push({ id: employee.id, fullName: employee.fullName })
        return acc
      },
      []
    ) ?? []

  return {
    loading: selectableEmployeesLoading || terminatedEmployeesLoading,
    selectableEmployeeIds: [...selectableEmployeeIds, ...terminatedEmployeeIds],
    selectableEmployees: [...selectableEmployees, ...terminatedEmployees],
  }
}

export { useSelectableEmployees }
